import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { User, Wallet, LogOut, CheckCircle } from "lucide-react";
import clsx from "clsx";
import { useDailyTask } from "../hooks/useDailyTask";
import { useAppForegroundHeartbeat } from "../hooks/useAppForegroundHeartbeat";
import { useAuthStore } from "../store/useAuthStore";
import { useProfileWalletQuery } from "../hooks/queries/useProfileWalletQuery";
import { WEB_AUTH_UI_ENABLED } from "../config/publicConfig";

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile: storeProfile, signOut, user } = useAuthStore();
  const { data: pw } = useProfileWalletQuery(user?.id);
  const profile = pw?.profile ?? storeProfile;

  useAppForegroundHeartbeat(!!user);

  const isMatchMode = location.pathname.includes("/match");
  useDailyTask(!!user, isMatchMode);

  const handleLogout = async () => {
    await signOut();
    navigate(WEB_AUTH_UI_ENABLED ? "/login" : "/");
  };

  const navItems = [
    { path: "/app/wallet", icon: Wallet, label: "钱包" },
    { path: "/app/profile", icon: User, label: "我的" },
  ];

  const isVerified = profile?.age_verified_status === "approved";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Verification Banner */}
      {profile &&
        !isVerified &&
        location.pathname !== "/app/verify-age" && (
          <div className="fixed top-14 left-0 right-0 bg-yellow-50 border-b border-yellow-100 px-4 py-2 z-40 flex items-center justify-between text-xs sm:text-sm">
            <div className="flex items-center gap-2 text-yellow-800">
              <span className="text-lg">⚠️</span>
              <span>
                {profile.age_verified_status === "submitted"
                  ? "身份信息正在审核中，请耐心等待。"
                  : "根据法律要求，您需要先完成年龄与身份验证才能使用完整功能。"}
              </span>
            </div>
            {profile.age_verified_status !== "submitted" && (
              <button
                onClick={() => navigate("/app/verify-age")}
                className="px-3 py-1 bg-yellow-600 text-white rounded-full font-medium hover:bg-yellow-700 transition-colors shrink-0"
              >
                去验证
              </button>
            )}
          </div>
        )}

      <header className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 z-50 px-4 flex items-center justify-between">
        <h1 className="text-lg font-bold text-pink-500">BamaBang</h1>
        <div className="flex items-center gap-4">
          {isVerified && (
            <div className="flex items-center gap-1 text-green-500 text-xs font-medium">
              <CheckCircle size={14} />
              <span>已认证</span>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-red-500 transition-colors"
          >
            <LogOut size={18} />
            <span>退出</span>
          </button>
        </div>
      </header>

      <main
        className={clsx(
          "flex-1 pt-16 pb-16",
          profile &&
            !isVerified &&
            location.pathname !== "/app/verify-age" &&
            "pt-24",
        )}
      >
        <Outlet />
      </main>

      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex justify-around items-center h-16 z-50">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <button
              key={item.path}
              onClick={() => {
                if (!isVerified && item.path !== "/app/profile") {
                  alert("请先完成年龄与身份验证！");
                  navigate("/app/verify-age");
                } else {
                  navigate(item.path);
                }
              }}
              className={clsx(
                "flex flex-col items-center justify-center w-full h-full space-y-1",
                isActive ? "text-pink-500" : "text-gray-500",
              )}
            >
              <item.icon size={24} />
              <span className="text-xs">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
}
